import React, { forwardRef, useId } from 'react';

const digitsOnly = (s = '') => (s || '').replace(/\D+/g, '');
const toApi = (digits) => (digits ? `+1${digits}` : ''); // keep +1 + partial while typing

// "(123) 456-7890"
const formatUS = (ten) => {
  const a = ten.slice(0, 3);
  const b = ten.slice(3, 6);
  const c = ten.slice(6, 10);
  if (!ten) return '';
  if (ten.length <= 3) return `(${a}`;
  if (ten.length <= 6) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
};

// Remove leading country code '1' from display if present (<= 11 digits)
const displayFromApi = (value) => {
  const raw = digitsOnly(value);
  if (!raw || raw === '1') return '+1';
  let ten = raw;
  if (raw.startsWith('1') && raw.length <= 11) ten = raw.slice(1);
  return '+1 ' + formatUS(ten.slice(0, 10));
};

const PhoneField = forwardRef(function PhoneField(
  {
    name = 'phone_number',
    value = '', // API format in parent: "+1XXXXXXXXXX" (partial ok)
    onChange, // will be called for both number and type changes
    onBlur,
    required = false,
    error = '',
    className = '',
    hint = 'US number: 10 digits',
    // Optional label text if phoneType is false
    label,
    // phone type UI (do not forward to DOM)
    ...rest
  },
  ref
) {
  const id = useId();

  // emit event-like shape for parent handleChange
  const emitNumber = (digits) => {
    const safe = digits.slice(0, 10);
    const apiValue = toApi(safe);
    onChange?.({ target: { name, value: apiValue } }, apiValue);
  };

  const handleInput = (e) => {
    // Remove '+1' if present in the input, then emit digits
    let val = e.target.value.replace(/^\+1\s?/, '');
    emitNumber(digitsOnly(val));
  };
  const handlePaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    e.preventDefault();
    emitNumber(digitsOnly(pasted));
  };

  return (
    <div className='flex flex-col gap-1'>
      {label && (
        <label htmlFor={id} className='go-label-16 mb-1 block'>
          {label}
          {/* {label} {required && <span aria-hidden='true'>*</span>} */}
        </label>
      )}

      <div className='flex items-center space-x-2'>
        {/* Phone input */}
        <input
          id={id}
          ref={ref}
          type='tel'
          inputMode='numeric'
          autoComplete='tel-national'
          name={name}
          value={displayFromApi(value)}
          onChange={handleInput}
          onPaste={handlePaste}
          onBlur={onBlur}
          required={required}
          placeholder='+1 (555) 123-4567'
          className={`go-inputtext-16 ${error ? 'ring-1 ring-red-500' : ''} ${className}`}
          aria-invalid={!!error}
          aria-describedby={hint ? `${id}-hint` : undefined}
          {...rest}
        />
      </div>
      {error && <p className='text-xs text-red-600'>{error}</p>}
    </div>
  );
});

export default PhoneField;
